<script lang="ts">
    import type { EntityDataProperties, EntityDataPropertiesKeys, EntityDataProperty } from "@workadventure/map-editor";
    import { onDestroy } from "svelte";
    import type { ApplicationDefinitionInterface } from "@workadventure/messages";
    import { v4 as uuid } from "uuid";
    import {
        mapEditorEntityModeStore,
        mapEditorSelectedEntityPrefabStore,
        mapEditorSelectedEntityStore,
    } from "../../../Stores/MapEditorStore";
    import { analyticsClient } from "../../../Administration/AnalyticsClient";
    import LL from "../../../../i18n/i18n-svelte";
    import AddPropertyButtonWrapper from "../PropertyEditor/AddPropertyButtonWrapper.svelte";
    import PlayAudioPropertyEditor from "../PropertyEditor/PlayAudioPropertyEditor.svelte";
    import OpenWebsitePropertyEditor from "../PropertyEditor/OpenWebsitePropertyEditor.svelte";

    import { IconChevronDown, IconArrowLeft } from "../../Icons";
    import Input from "../../Input/Input.svelte";
    import TextArea from "../../Input/TextArea.svelte";
    import InputSwitch from "../../Input/InputSwitch.svelte";
    import OpenFilePropertyEditor from "../PropertyEditor/OpenFilePropertyEditor.svelte";
    import type { Entity } from "../../../Phaser/ECS/Entity";
    import { gameManager } from "../../../Phaser/Game/GameManager";

    const applicationManager = gameManager.getCurrentGameScene().applicationManager;

    let properties: EntityDataProperties = $state([]);
    let entityName = $state("");
    let entityDescription = $state("");
    let entitySearchable = $state(false);
    let showDescriptionField = $state(false);
    let selectedEntity: Entity | undefined = undefined;

    let selectedEntityUnsubscriber = mapEditorSelectedEntityStore.subscribe((currentEntity) => {
        if (currentEntity) {
            currentEntity.setEditColor(0x00ffff);
            properties = currentEntity.getProperties() ?? [];
            entityName = currentEntity.getEntityData().name ?? "";
            const descriptionProperty = properties.find((p) => p.type === "entityDescriptionProperties");
            if (!descriptionProperty) {
                $mapEditorSelectedEntityStore?.addProperty({
                    id: uuid(),
                    type: "entityDescriptionProperties",
                    description: "",
                    searchable: false,
                });
            } else {
                entityDescription = descriptionProperty.description ?? "";
                entitySearchable = descriptionProperty.searchable ?? false;
            }
            selectedEntity = currentEntity;
        }
    });

    function onAddProperty(type: EntityDataPropertiesKeys, subtype?: string) {
        if ($mapEditorSelectedEntityStore) {
            analyticsClient.addMapEditorProperty("entity", type || "unknown");
            const property = getPropertyFromType(type, subtype);
            $mapEditorSelectedEntityStore.addProperty(property);

            // refresh properties
            properties = $mapEditorSelectedEntityStore?.getProperties();
        }
    }

    function onAddSpecificProperty(app: ApplicationDefinitionInterface) {
        if (!$mapEditorSelectedEntityStore) return;
        analyticsClient.addMapEditorProperty("entity", app.name);
        const property: EntityDataProperty = {
            id: uuid(),
            type: "openWebsite",
            application: app.name,
            closable: true,
            buttonLabel: app.name,
            link: "",
            newTab: false,
            placeholder: app.description,
            label: app.name,
            policy: app.policy,
            icon: app.image,
            regexUrl: app.regexUrl,
            targetEmbedableUrl: app.targetUrl,
            forceNewTab: app.forceNewTab,
            allowAPI: app.allowAPI,
            hideUrl: false,
        };
        $mapEditorSelectedEntityStore.addProperty(property);

        // refresh properties
        properties = $mapEditorSelectedEntityStore?.getProperties();
    }

    function onUpdateName() {
        if ($mapEditorSelectedEntityStore) {
            $mapEditorSelectedEntityStore.setEntityName(entityName);
        }
    }

    function onUpdateDescription() {
        let properties = $mapEditorSelectedEntityStore
            ?.getProperties()
            .find((p) => p.type === "entityDescriptionProperties");
        if (!properties || (properties && properties.type !== "entityDescriptionProperties"))
            throw new Error("Wrong property type");

        properties.description = entityDescription;
        if ($mapEditorSelectedEntityStore) {
            $mapEditorSelectedEntityStore.updateProperty($state.snapshot(properties));
        }
    }

    function onUpdateSearchable() {
        let properties = $mapEditorSelectedEntityStore
            ?.getProperties()
            .find((p) => p.type === "entityDescriptionProperties");
        if (!properties || (properties && properties.type !== "entityDescriptionProperties"))
            throw new Error("Wrong property type");

        properties.searchable = entitySearchable;
        if ($mapEditorSelectedEntityStore) {
            $mapEditorSelectedEntityStore.updateProperty($state.snapshot(properties));
        }
    }

    function onUpdateProperty(property: EntityDataProperty) {
        if ($mapEditorSelectedEntityStore) {
            $mapEditorSelectedEntityStore.updateProperty($state.snapshot(property));
        }
    }

    function getPropertyFromType(type: EntityDataPropertiesKeys, subtype?: string): EntityDataProperty {
        const id = uuid();
        let placeholder: string;
        let buttonLabel: string;
        let policy: string | undefined;
        switch (type) {
            case "jitsiRoomProperty":
                return {
                    id,
                    type,
                    jitsiRoomConfig: {},
                    closable: true,
                    roomName: "JITSI ROOM",
                    buttonLabel: $LL.mapEditor.properties.jitsiRoomProperty.label(),
                };
            case "livekitRoomProperty":
                return {
                    id,
                    type,
                    roomName: "LIVEKIT ROOM",
                    buttonLabel: $LL.mapEditor.properties.livekitRoomProperty.label(),
                    livekitRoomConfig: {
                        startWithAudioMuted: false,
                        startWithVideoMuted: false,
                        disableChat: false,
                    },
                    livekitRoomAdminTag: "",
                };
            case "openFile":
                return {
                    id,
                    type,
                    link: "",
                    name: "",
                    closable: true,
                    newTab: false,
                    buttonLabel: $LL.mapEditor.properties.openFile.label(),
                    policy,
                    width: 50,
                    hideUrl: false,
                };
            case "openWebsite":
                switch (subtype) {
                    case "youtube":
                        placeholder = "https://www.youtube.com/watch?v=Y9ubBWf5w20";
                        buttonLabel = $LL.mapEditor.properties.youtube.label();
                        policy =
                            "fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;";
                        break;
                    case "klaxoon":
                        placeholder = "https://app.klaxoon.com/";
                        buttonLabel = $LL.mapEditor.properties.klaxoon.label();
                        break;
                    case "googleDrive":
                        placeholder = "https://drive.google.com/file/d/1DjNjZVbVeQO9EvgONLzCtl6wG-kxSr9Z/preview";
                        buttonLabel = $LL.mapEditor.properties.googleDrive.label();
                        break;
                    case "googleDocs":
                        placeholder =
                            "https://docs.google.com/document/d/1iFHmKL4HJ6WzvQI-6FlyeuCy1gzX8bWQ83dNlcTzigk/edit";
                        buttonLabel = $LL.mapEditor.properties.googleDocs.label();
                        break;
                    case "googleSheets":
                        placeholder =
                            "https://docs.google.com/spreadsheets/d/1SBIn3IBG30eeq944OhT4VI_tSg-b1CbB0TV0ejK70RA/edit";
                        buttonLabel = $LL.mapEditor.properties.googleSheets.label();
                        break;
                    case "googleSlides":
                        placeholder =
                            "https://docs.google.com/presentation/d/1fU4fOnRiDIvOoVXbksrF2Eb0L8BYavs7YSsBmR_We3g/edit";
                        buttonLabel = $LL.mapEditor.properties.googleSlides.label();
                        break;
                    case "eraser":
                        placeholder = "https://app.eraser.io/workspace/ExSd8Z4wPsaqMMgTN4VU";
                        buttonLabel = $LL.mapEditor.properties.eraser.label();
                        break;
                    case "excalidraw":
                        placeholder = "https://excalidraw.workadventu.re/";
                        buttonLabel = $LL.mapEditor.properties.excalidraw.label();
                        break;
                    case "cards":
                        placeholder =
                            "https://member.workadventu.re?tenant=<your cards tenant>&learning=<Your cards learning>";
                        buttonLabel = $LL.mapEditor.properties.cards.label();
                        break;
                    default:
                        placeholder = "https://workadventu.re";
                        buttonLabel = $LL.mapEditor.properties.openWebsite.label();
                }
                return {
                    id,
                    type,
                    closable: true,
                    buttonLabel,
                    link: "",
                    newTab: false,
                    application: subtype ?? "website",
                    placeholder,
                    forceNewTab: false,
                    allowAPI: false,
                    policy,
                    width: 50,
                    hideUrl: false,
                };
            case "playAudio":
                return {
                    id,
                    type,
                    buttonLabel: $LL.mapEditor.properties.playAudio.label(),
                    audioLink: "",
                    volume: 1,
                };
            default:
                throw new Error(`Unknown property type ${type}`);
        }
    }

    function onDeleteProperty(id: string) {
        if ($mapEditorSelectedEntityStore) {
            analyticsClient.removeMapEditorProperty("entity", properties.find((p) => p.id === id)?.type || "unknown");
            $mapEditorSelectedEntityStore.deleteProperty(id);
            // refresh properties
            properties = $mapEditorSelectedEntityStore?.getProperties();
            // $mapEditorSelectedEntityStore.delete();
            // mapEditorSelectedEntityStore.set(undefined);
            // mapEditorEntityModeStore.set("ADD");
        }
    }

    function backToSelectObject() {
        mapEditorSelectedEntityStore.set(undefined);
        mapEditorSelectedEntityPrefabStore.set(undefined);
        mapEditorEntityModeStore.set("ADD");
    }

    onDestroy(() => {
        selectedEntityUnsubscriber();
        selectedEntity?.removeEditColor();
    });

    function toggleDescriptionField() {
        showDescriptionField = !showDescriptionField;
    }
</script>

{#if $mapEditorSelectedEntityStore === undefined}
    {$LL.mapEditor.entityEditor.editInstructions()}
{:else}
    <div class="overflow-x-hidden overflow-y-auto">
        <div class="header-container">
            <h3>{$LL.mapEditor.entityEditor.editing({ name: $mapEditorSelectedEntityStore.getPrefab().name })}</h3>
        </div>
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <p
            onclick={(event) => {
                event.preventDefault();
                backToSelectObject();
            }}
            class="flex flex-row items-center text-xs m-0"
        >
            <IconArrowLeft font-size="12" class="cursor-pointer" />
            <span class="ml-1 cursor-pointer">{$LL.mapEditor.entityEditor.itemPicker.backToSelectObject()}</span>
        </p>
        <div class="properties-buttons flex flex-row m-2">
            <AddPropertyButtonWrapper
                property="playAudio"
                onclick={() => {
                    onAddProperty("playAudio");
                }}
            />
        </div>
        <div class="properties-buttons flex flex-row flex-wrap m-2">
            <AddPropertyButtonWrapper
                property="openWebsite"
                onclick={() => {
                    onAddProperty("openWebsite");
                }}
            />
            <AddPropertyButtonWrapper
                property="openFile"
                onclick={() => {
                    onAddProperty("openFile");
                }}
            />
            <AddPropertyButtonWrapper
                property="openWebsite"
                subProperty="klaxoon"
                onclick={() => {
                    onAddProperty("openWebsite", "klaxoon");
                }}
            />
            <AddPropertyButtonWrapper
                property="openWebsite"
                subProperty="youtube"
                onclick={() => {
                    onAddProperty("openWebsite", "youtube");
                }}
            />
            <AddPropertyButtonWrapper
                property="openWebsite"
                subProperty="googleDrive"
                onclick={() => {
                    onAddProperty("openWebsite", "googleDrive");
                }}
            />
            <AddPropertyButtonWrapper
                property="openWebsite"
                subProperty="googleDocs"
                onclick={() => {
                    onAddProperty("openWebsite", "googleDocs");
                }}
            />
            <AddPropertyButtonWrapper
                property="openWebsite"
                subProperty="googleSheets"
                onclick={() => {
                    onAddProperty("openWebsite", "googleSheets");
                }}
            />
            <AddPropertyButtonWrapper
                property="openWebsite"
                subProperty="googleSlides"
                onclick={() => {
                    onAddProperty("openWebsite", "googleSlides");
                }}
            />
            <AddPropertyButtonWrapper
                property="openWebsite"
                subProperty="eraser"
                onclick={() => {
                    onAddProperty("openWebsite", "eraser");
                }}
            />
            <AddPropertyButtonWrapper
                property="openWebsite"
                subProperty="excalidraw"
                onclick={() => {
                    onAddProperty("openWebsite", "excalidraw");
                }}
            />
            <AddPropertyButtonWrapper
                property="openWebsite"
                subProperty="tldraw"
                onclick={() => {
                    onAddProperty("openWebsite", "tldraw");
                }}
            />
        </div>
        <div class="properties-buttons flex flex-row flex-wrap m-2">
            {#each applicationManager.applications as app, index (`my-own-app-${index}`)}
                <AddPropertyButtonWrapper
                    property="openWebsite"
                    subProperty={app.name}
                    onclick={() => {
                        onAddSpecificProperty(app);
                    }}
                />
            {/each}
        </div>
        <div class="entity-name-container">
            <Input
                id="objectName"
                label={$LL.mapEditor.entityEditor.objectName()}
                type="text"
                placeholder={$LL.mapEditor.entityEditor.objectNamePlaceholder()}
                bind:value={entityName}
                onchange={onUpdateName}
            />
        </div>
        <div class="entity-name-container">
            {#if !showDescriptionField}
                <a
                    href="#addDescriptionField"
                    class="pl-0 text-blue-500 flex flex-row items-center"
                    onclick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleDescriptionField();
                    }}>+ {$LL.mapEditor.entityEditor.addDescriptionField()}</a
                >
            {:else}
                <button class="pl-0 text-blue-500 flex flex-row items-center" onclick={toggleDescriptionField}>
                    <IconChevronDown />{$LL.mapEditor.entityEditor.addDescriptionField()}</button
                >

                <TextArea
                    label={$LL.mapEditor.entityEditor.objectDescription()}
                    id="objectDescription"
                    placeHolder={$LL.mapEditor.entityEditor.objectDescriptionPlaceholder()}
                    bind:value={entityDescription}
                    onchange={onUpdateDescription}
                    onkeypress={() => {}}
                />
            {/if}
        </div>

        <InputSwitch
            label={$LL.mapEditor.entityEditor.objectSearchable()}
            id="searchable"
            bind:value={entitySearchable}
            onchange={onUpdateSearchable}
        />

        <div class="properties-container flex flex-col gap-8 p-1">
            {#each properties as property, i (property.id)}
                {#if property.type !== "entityDescriptionProperties"}
                    <div class="property-box border border-solid border-white/20 bg-white/5 rounded p-2">
                        {#if properties[i].type === "playAudio"}
                            <PlayAudioPropertyEditor
                                bind:property={properties[i]}
                                onclose={() => {
                                    onDeleteProperty(property.id);
                                }}
                                onchange={() => onUpdateProperty(properties[i])}
                            />
                        {:else if properties[i].type === "openWebsite"}
                            <OpenWebsitePropertyEditor
                                bind:property={properties[i]}
                                triggerOptionActivated={false}
                                onclose={() => {
                                    onDeleteProperty(property.id);
                                }}
                                onchange={() => onUpdateProperty(properties[i])}
                            />
                        {:else if properties[i].type === "openFile"}
                            <OpenFilePropertyEditor
                                bind:property={properties[i]}
                                onclose={() => {
                                    onDeleteProperty(property.id);
                                }}
                                onchange={() => onUpdateProperty(properties[i])}
                            />
                        {/if}
                    </div>
                {/if}
            {/each}
        </div>
    </div>
{/if}

<style>
    .properties-container {
        overflow-y: auto;
        overflow-x: hidden;
    }

    .properties-container::-webkit-scrollbar {
        display: none;
    }

    .entity-name-container {
        display: flex;
        width: 100%;
        margin-bottom: 0.5em;
        margin-top: 0.5em;
        flex-direction: column;

        * {
            margin-bottom: 0;
        }
    }
</style>
